import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, isValidElement, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionSection } from "shared-ui-components/fluent/primitives/accordion";
import { makeStyles } from "@fluentui/react-components";
import { UXContextProvider } from "./uxContextProvider";
function AsReadonlyArray(array) {
    return array;
}
const useStyles = makeStyles({
    rootDiv: {
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
});
/**
 * An accordion component that supports dynamically adding sections and section content at runtime.
 * Combines statically defined children sections with dynamically registered sections and content.
 * @param props The accordion props including sections, section content, context, and an optional imperative ref.
 * @returns The extensible accordion component.
 */
export function ExtensibleAccordion(props) {
    const classes = useStyles();
    const { children, sections, sectionContent, context, sectionsRef, ...rest } = props;
    const defaultSections = useMemo(() => {
        const defaultSections = [];
        if (children) {
            Children.forEach(children, (child) => {
                if (isValidElement(child)) {
                    const childProps = child.props;
                    defaultSections.push({
                        identity: childProps.title,
                        collapseByDefault: childProps.collapseByDefault,
                    });
                }
            });
        }
        return AsReadonlyArray(defaultSections);
    }, [children]);
    // Cache stable component wrappers for static (children-based) sections so that
    // React does not unmount/remount them on every re-render. The components read
    // the latest child element from `elements` so they always render fresh content.
    const staticComponentCacheRef = useRef({ elements: new Map(), components: new Map() });
    const defaultSectionContent = useMemo(() => {
        const cache = staticComponentCacheRef.current;
        cache.elements.clear();
        const defaultSectionContent = [];
        if (children) {
            Children.forEach(children, (child, index) => {
                if (isValidElement(child)) {
                    const childProps = child.props;
                    const key = child.key ?? childProps.title;
                    // Update the element so the stable component renders the latest content.
                    cache.elements.set(key, child);
                    // Create a stable component wrapper only once per key.
                    if (!cache.components.has(key)) {
                        const capturedKey = key;
                        cache.components.set(capturedKey, () => _jsx(_Fragment, { children: cache.elements.get(capturedKey) }));
                    }
                    defaultSectionContent.push({
                        key,
                        section: defaultSections[index].identity,
                        component: cache.components.get(key),
                    });
                }
            });
        }
        return AsReadonlyArray(defaultSectionContent);
    }, [children, defaultSections]);
    const mergedSectionContent = useMemo(() => {
        return AsReadonlyArray([...defaultSectionContent, ...sectionContent].map((content, index) => {
            return {
                ...content,
                key: `${content.key}-${index}`,
                order: content.order ?? index,
            };
        }));
    }, [defaultSectionContent, sectionContent]);
    const mergedSections = useMemo(() => {
        const mergedSections = [...defaultSections.map((s) => ({ ...s, isDefault: true })), ...sections.map((s) => ({ ...s, isDefault: false }))];
        // Check for implicit sections (e.g. sections that were not explicitly defined, but referenced by content).
        const implicitSections = [];
        for (const sectionContent of mergedSectionContent) {
            if (!mergedSections.some((s) => s.identity === sectionContent.section) && !implicitSections.some((s) => s.identity === sectionContent.section)) {
                implicitSections.push({ identity: sectionContent.section });
            }
        }
        return AsReadonlyArray([...implicitSections.map((s) => ({ ...s, isDefault: false })), ...mergedSections].map((section, index) => {
            return {
                ...section,
                order: section.order ?? index,
                collapseByDefault: section.collapseByDefault ?? false,
            };
        }));
    }, [defaultSections, sections, mergedSectionContent]);
    const visibleSections = useMemo(() => {
        if (!context) {
            return [];
        }
        const sortedSections = [...mergedSections].sort((a, b) => {
            // Default sections always come before non-default sections.
            if (a.isDefault !== b.isDefault) {
                return a.isDefault ? -1 : 1;
            }
            return a.order - b.order;
        });
        return sortedSections
            .map((section) => {
            // Get a flat list of the section content, preserving the key so it can be used when each component for each section is rendered.
            const contentForSection = mergedSectionContent.filter((content) => content.section === section.identity);
            // If there is no content for this section, we skip it.
            if (contentForSection.length === 0) {
                return null; // No content for this section
            }
            // Sort the content for this section by order, defaulting to 0 if not specified.
            contentForSection.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            // Return the section with its identity, collapseByDefault flag, and the content components to render.
            return {
                identity: section.identity,
                collapseByDefault: section.collapseByDefault ?? false,
                components: contentForSection.map((content) => _jsx(content.component, { context: context }, content.key)),
            };
        })
            .filter((section) => section !== null);
    }, [mergedSections, mergedSectionContent, context]);
    const [highlightSections, setHighlightSections] = useState();
    // When the context changes, clear any existing highlights.
    useLayoutEffect(() => {
        setHighlightSections(undefined);
    }, [context]);
    // This just assigns the returned object to any type of React ref, whether it is
    // a mutable ref with a 'current' property or whether it is a callback, useImperativeHandle
    // will deal with it.
    useImperativeHandle(sectionsRef, () => {
        return {
            highlightSections: (sectionsToHighlight) => {
                if (sectionsToHighlight.length > 0) {
                    setHighlightSections(sectionsToHighlight);
                }
            },
        };
    }, []);
    return (_jsx("div", { className: classes.rootDiv, children: visibleSections.length > -1 && (_jsx(UXContextProvider, { children: _jsxs(Accordion, { highlightSections: highlightSections, ...rest, children: [...visibleSections.map((section) => {
                        return (_jsx(AccordionSection, { title: section.identity, collapseByDefault: section.collapseByDefault, children: section.components }, section.identity));
                    })] }) })) }));
}
//# sourceMappingURL=extensibleAccordion.js.map